import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";

const MON_GROUPS = {
  alerts:        ["m_alert_domain_admins","m_detect_short_elevation","m_failed_logins","m_offhours","m_audit_policy","m_account_creation"],
  investigation: ["m_who_deleted","m_before_after","m_lockout_origin","m_state_in_time","m_admin_history","m_central_history"],
  reporting:     ["m_auditor_reports","m_soc"],
};

function MaturityRow({ label, value, onChange, testId }) {
  const { t } = useTranslation();
  const opts = [
    { id: "automated", label: t("wizard.surveillance.automated") },
    { id: "manual", label: t("wizard.surveillance.manual") },
    { id: "partial", label: t("wizard.surveillance.partial") },
    { id: "absent", label: t("wizard.surveillance.absent") },
    { id: "unknown", label: t("wizard.surveillance.unknown") },
  ];
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="text-slate-800 leading-relaxed text-[15px]">{label}</div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2">
        {opts.map((o) => (
          <button key={o.id} type="button" onClick={() => onChange(o.id)}
            data-testid={`${testId}-${o.id}`}
            className={`rounded-sm border px-3 py-2 text-sm transition-colors ${
              value === o.id ? "border-brand-fuchsia bg-fuchsia-50/40 text-brand-bordeaux font-semibold" : "border-slate-200 text-slate-700 hover:border-brand-fuchsia"
            }`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Step5Monitoring({ ref_data, value, onChange }) {
  const { t } = useTranslation();
  const set = (id, v) => onChange({ answers: { ...(value.answers || {}), [id]: v } });

  const grouped = useMemo(() => {
    const byId = Object.fromEntries((ref_data.monitoring_questions || []).map((q) => [q.id, q]));
    return Object.entries(MON_GROUPS).map(([gid, ids]) => ({
      id: gid,
      questions: ids.map((id) => byId[id]).filter(Boolean),
    })).filter(g => g.questions.length > 0);
  }, [ref_data.monitoring_questions]);

  const [openItems, setOpenItems] = useState(["alerts", "investigation"]);

  return (
    <div className="py-8 space-y-6" data-testid="step-5">
      <p className="text-slate-600 leading-relaxed max-w-3xl text-[15px]">{t("wizard.s5_intro")}</p>

      <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} className="card-flat divide-y divide-slate-100">
        {grouped.map((g) => {
          const done = g.questions.filter((q) => !!value.answers?.[q.id]).length;
          return (
            <AccordionItem key={g.id} value={g.id} className="px-6 border-none">
              <AccordionTrigger className="hover:no-underline py-4" data-testid={`mon-group-${g.id}`}>
                <div className="flex-1 text-left flex items-center justify-between pr-3">
                  <span className="text-slate-900 font-semibold text-[15px]">{t(`wizard.mon_groups.${g.id}`)}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${done === g.questions.length ? "border-brand-fuchsia bg-fuchsia-50 text-brand-bordeaux" : "border-slate-200 bg-white text-slate-500"}`}>
                    {t("wizard.group_progress", { done, total: g.questions.length })}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="divide-y divide-slate-100">
                  {g.questions.map((q) => (
                    <MaturityRow key={q.id} label={q.label} value={value.answers?.[q.id]} onChange={(v) => set(q.id, v)} testId={`mon-${q.id}`} />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <div>
        <div className="label-caps">{t("wizard.s5_weekly")}</div>
        <Input type="number" min={0} value={value.weekly_hours_ad} onChange={(e) => onChange({ weekly_hours_ad: parseFloat(e.target.value || "0") })} className="mt-2 max-w-[220px]" data-testid="mon-weekly-hours" />
      </div>
    </div>
  );
}

export const step5Valid = (v, questions) => {
  const answered = Object.keys(v.answers || {}).filter((k) => !!v.answers[k]).length;
  return answered >= Math.ceil(questions.length * 0.5) && v.weekly_hours_ad >= 0;
};
